import { AppIcon } from '@/components/ui/app-icon';
import { iconShieldExclamation, iconWifiSlash } from '@/lib/icons';
import { Button } from '@/components/ui/button';
import { BRAND } from '@/lib/brand';

export type HealthCheckDialogProps = {
  kind: 'connection-lost' | 'account-restricted';
  /** Replaces the kind's own line under the title. */
  description?: string;
  onPrimaryAction: () => void;
  onDismiss?: () => void;
};

export function HealthCheckDialog({
  kind,
  description,
  onPrimaryAction,
  onDismiss,
}: HealthCheckDialogProps) {
  const connectionLost = kind === 'connection-lost';

  return (
    <>
      <div className='fixed inset-0 z-[9999] bg-black/50 backdrop-blur-[8px]' />
      <section
        role='alertdialog'
        aria-modal='true'
        aria-labelledby='health-check-title'
        className='fixed top-1/2 left-1/2 z-[10000] w-[90%] max-w-[450px] -translate-x-1/2 -translate-y-1/2 animate-in rounded-2xl border border-border bg-card p-8 font-sans text-card-foreground shadow-2xl duration-200 fade-in-0 zoom-in-95 motion-reduce:animate-none'
      >
        <div className='mb-5 flex items-start gap-4'>
          <div className='rounded-xl border border-destructive/20 bg-destructive/10 p-2.5 text-destructive'>
            <AppIcon
              icon={connectionLost ? iconWifiSlash : iconShieldExclamation}
              className='text-[24px]'
            />
          </div>
          <div>
            <h2
              id='health-check-title'
              className='text-xl font-semibold tracking-tight'
            >
              {connectionLost ? 'Connection lost' : 'Account restricted'}
            </h2>
            <p className='mt-1 text-sm text-muted-foreground'>
              {description ??
                (connectionLost
                  ? `Can't reach ${BRAND.dotName} right now.`
                  : 'Access is paused while we review activity. It usually returns within 24 hours.')}
            </p>
          </div>
        </div>

        <div className='mb-6 flex flex-wrap gap-3 rounded-xl border border-border bg-muted p-4 text-sm'>
          <a
            href='mailto:support@cleanlist.ai'
            className='text-primary hover:underline'
          >
            Contact support
          </a>
          {connectionLost && (
            <a
              href='https://docs.cleanlist.ai'
              className='text-primary hover:underline'
            >
              Help center
            </a>
          )}
        </div>

        <div className='flex gap-3'>
          <Button
            variant='brand'
            className='flex-1 rounded-lg'
            onClick={onPrimaryAction}
          >
            {connectionLost ? 'Reload page' : 'Contact support'}
          </Button>
          {onDismiss && (
            <Button
              variant='outline'
              className='flex-1 rounded-lg text-foreground'
              onClick={onDismiss}
            >
              Dismiss
            </Button>
          )}
        </div>
      </section>
    </>
  );
}
